import AddIcon from '@mui/icons-material/Add';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import DownloadIcon from '@mui/icons-material/Download';
import RemoveIcon from '@mui/icons-material/Remove';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import {
  alpha,
  Box,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import LFCIconButton from '_components/inputs/LFCIconButton';
import {getLanguageByFileName, getSyntaxColors, Token} from '_logics/code-highlight';
import {memo, useMemo, useState} from 'react';

type ViewMode = 'unified' | 'sideBySide';

type DiffDisplayProps = {
  beforeContent: string;
  afterContent: string;
  changeType?: 'A' | 'M' | 'D';
  maxHeight?: number | string;
  fileName?: string | null;
  /** Beforeファイルのダウンロードコールバック（undefinedまたはnullの場合はボタン非活性） */
  onDownloadBefore?: (() => void) | null;
  /** Afterファイルのダウンロードコールバック（undefinedまたはnullの場合はボタン非活性） */
  onDownloadAfter?: (() => void) | null;
  /** Before側に表示するコミットリスト（オプション） */
  beforeSidebar?: React.ReactNode;
  /** After側に表示するコミットリスト（オプション） */
  afterSidebar?: React.ReactNode;
  /** 表示モード（外部制御用、指定時は内部状態を使用しない） */
  viewMode?: ViewMode;
  /** フォントサイズ（外部制御用、指定時は内部状態を使用しない） */
  fontSize?: number;
  /** ツールバーを非表示にする */
  hideToolbar?: boolean;
  /** ローディング中フラグ */
  loading?: boolean;
  /** AI解説ボタンのコールバック */
  onAiExplain?: () => void;
  /** AI解説パネルが開いているか */
  aiOpen?: boolean;
  /** AI解説ローディング中 */
  aiLoading?: boolean;
};

/**
 * 行単位の差分を計算する（GitHub方式: delete/add/sameのみ）
 */
type DiffLine = {
  type: 'add' | 'delete' | 'same';
  content: string;
  beforeLineNum?: number;
  afterLineNum?: number;
};

const computeLineDiff = (before: string, after: string): DiffLine[] => {
  const beforeLines = before ? before.split('\n') : [];
  const afterLines = after ? after.split('\n') : [];
  const result: DiffLine[] = [];

  let beforeIdx = 0;
  let afterIdx = 0;

  while (beforeIdx < beforeLines.length || afterIdx < afterLines.length) {
    if (beforeIdx >= beforeLines.length) {
      // beforeが終わった - 残りは追加
      result.push({type: 'add', content: afterLines[afterIdx], afterLineNum: afterIdx + 1});
      afterIdx++;
    } else if (afterIdx >= afterLines.length) {
      // afterが終わった - 残りは削除
      result.push({type: 'delete', content: beforeLines[beforeIdx], beforeLineNum: beforeIdx + 1});
      beforeIdx++;
    } else if (beforeLines[beforeIdx] === afterLines[afterIdx]) {
      // 同じ行
      result.push({
        type: 'same',
        content: beforeLines[beforeIdx],
        beforeLineNum: beforeIdx + 1,
        afterLineNum: afterIdx + 1
      });
      beforeIdx++;
      afterIdx++;
    } else {
      // 異なる行 - 先読みして一致を探す
      let foundInAfter = -1;
      let foundInBefore = -1;

      // afterの次の数行でbeforeLines[beforeIdx]を探す
      for (let i = afterIdx + 1; i < Math.min(afterIdx + 5, afterLines.length); i++) {
        if (afterLines[i] === beforeLines[beforeIdx]) {
          foundInAfter = i;
          break;
        }
      }

      // beforeの次の数行でafterLines[afterIdx]を探す
      for (let i = beforeIdx + 1; i < Math.min(beforeIdx + 5, beforeLines.length); i++) {
        if (beforeLines[i] === afterLines[afterIdx]) {
          foundInBefore = i;
          break;
        }
      }

      if (
        foundInAfter !== -1 &&
        (foundInBefore === -1 || foundInAfter - afterIdx <= foundInBefore - beforeIdx)
      ) {
        // afterに追加行がある
        while (afterIdx < foundInAfter) {
          result.push({type: 'add', content: afterLines[afterIdx], afterLineNum: afterIdx + 1});
          afterIdx++;
        }
      } else if (foundInBefore !== -1) {
        // beforeに削除行がある
        while (beforeIdx < foundInBefore) {
          result.push({
            type: 'delete',
            content: beforeLines[beforeIdx],
            beforeLineNum: beforeIdx + 1
          });
          beforeIdx++;
        }
      } else {
        // 一致が見つからない - 削除と追加のペアとして扱う（GitHub方式）
        result.push({
          type: 'delete',
          content: beforeLines[beforeIdx],
          beforeLineNum: beforeIdx + 1
        });
        result.push({type: 'add', content: afterLines[afterIdx], afterLineNum: afterIdx + 1});
        beforeIdx++;
        afterIdx++;
      }
    }
  }

  return result;
};

/**
 * 差分表示の共通コンポーネント
 */
const DiffDisplay = memo(
  ({
    beforeContent,
    afterContent,
    changeType,
    maxHeight = 400,
    fileName = null,
    onDownloadBefore,
    onDownloadAfter,
    beforeSidebar,
    afterSidebar,
    viewMode: externalViewMode,
    fontSize: externalFontSize,
    hideToolbar = false,
    loading = false,
    onAiExplain,
    aiOpen = false,
    aiLoading = false
  }: DiffDisplayProps) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const language = getLanguageByFileName(fileName);
    const syntaxColors = useMemo(() => getSyntaxColors(isDark), [isDark]);

    const renderTokens = (tokens: Token[]): React.ReactNode => {
      return tokens.map((token, index) => {
        if (token.type === 'text') {
          return <span key={index}>{token.value}</span>;
        }
        const color = syntaxColors[token.type as keyof typeof syntaxColors];
        return (
          <span key={index} style={{color}}>
            {token.value}
          </span>
        );
      });
    };

    const renderLineContent = (content: string): React.ReactNode => {
      if (!language) return content || ' ';
      return renderTokens(language.tokenizeLine(content));
    };

    // 内部状態（外部制御がない場合に使用）
    const [internalViewMode, setInternalViewMode] = useState<ViewMode>('sideBySide');
    const [internalZoomPercent, setInternalZoomPercent] = useState(100);

    // 外部制御または内部状態を使用
    const viewMode = externalViewMode ?? internalViewMode;
    const fontSize = externalFontSize ?? (internalZoomPercent / 100) * 14;

    const handleZoomIn = () => setInternalZoomPercent(prev => Math.min(prev + 5, 120));
    const handleZoomOut = () => setInternalZoomPercent(prev => Math.max(prev - 5, 80));
    const handleResetZoom = () => setInternalZoomPercent(100);

    // ホバー中の行インデックス
    const [hoveredLine, setHoveredLine] = useState<number | null>(null);

    const diffLines = computeLineDiff(beforeContent, afterContent);

    // 両方とも空の場合のみ「変更はありません」を表示（ローディング中は除く）
    if (diffLines.length === 0 && !beforeContent && !afterContent && !loading) {
      return (
        <Box sx={{p: 2, textAlign: 'center', color: 'text.secondary'}}>
          <Typography>変更はありません</Typography>
        </Box>
      );
    }

    // Side-by-Side用に行を整理
    type LineType = 'delete' | 'same' | 'empty' | 'add';
    const sideBySideLines: {
      before: {content: string; type: LineType; lineNum?: number};
      after: {content: string; type: LineType; lineNum?: number};
    }[] = [];

    let i = 0;
    while (i < diffLines.length) {
      const line = diffLines[i];

      if (line.type === 'same') {
        sideBySideLines.push({
          before: {content: line.content, type: 'same', lineNum: line.beforeLineNum},
          after: {content: line.content, type: 'same', lineNum: line.afterLineNum}
        });
        i++;
      } else if (line.type === 'delete') {
        // 次の行がaddなら、ペアとして同じ行に表示
        if (i + 1 < diffLines.length && diffLines[i + 1].type === 'add') {
          const nextLine = diffLines[i + 1];
          sideBySideLines.push({
            before: {content: line.content, type: 'delete', lineNum: line.beforeLineNum},
            after: {content: nextLine.content, type: 'add', lineNum: nextLine.afterLineNum}
          });
          i += 2;
        } else {
          sideBySideLines.push({
            before: {content: line.content, type: 'delete', lineNum: line.beforeLineNum},
            after: {content: '', type: 'empty'}
          });
          i++;
        }
      } else if (line.type === 'add') {
        sideBySideLines.push({
          before: {content: '', type: 'empty'},
          after: {content: line.content, type: 'add', lineNum: line.afterLineNum}
        });
        i++;
      } else {
        i++;
      }
    }

    return (
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        {/* ツールバー（hideToolbarがfalseの場合のみ表示） */}
        {!hideToolbar && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              px: 1,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: isDark ? 'grey.800' : 'grey.200'
            }}
          >
            <Tooltip title="縮小">
              <span>
                <LFCIconButton
                  color="primary"
                  size="small"
                  onClick={() => handleZoomOut()}
                  disabled={internalZoomPercent <= 80}
                >
                  <RemoveIcon />
                </LFCIconButton>
              </span>
            </Tooltip>
            <Tooltip title="リセット">
              <Typography
                variant="body1"
                sx={{textAlign: 'center', mx: 1, fontWeight: 'bold', cursor: 'pointer'}}
                onClick={handleResetZoom}
              >
                {internalZoomPercent}%
              </Typography>
            </Tooltip>
            <Tooltip title="拡大">
              <span>
                <LFCIconButton
                  color="primary"
                  size="small"
                  onClick={() => handleZoomIn()}
                  disabled={internalZoomPercent >= 120}
                >
                  <AddIcon />
                </LFCIconButton>
              </span>
            </Tooltip>
            <Box sx={{width: '1em'}} />
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, newMode) => newMode && setInternalViewMode(newMode)}
              size="small"
              color="primary"
            >
              <ToggleButton value="unified">
                <Tooltip title="行単位表示">
                  <ViewStreamIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="sideBySide">
                <Tooltip title="横並び表示">
                  <ViewColumnIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Unified表示（行単位） */}
        {viewMode === 'unified' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              overflow: 'hidden'
            }}
          >
            {/* 固定ヘッダー */}
            <Box sx={{display: 'flex', flexShrink: 0}}>
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1,
                  bgcolor: 'error.main',
                  color: 'error.contrastText',
                  borderRight: 1,
                  borderRightColor: isDark ? 'grey.700' : 'grey.300'
                }}
              >
                <Typography variant="body1">Before</Typography>
                <Tooltip title={onDownloadBefore ? 'ダウンロード' : 'ファイルが存在しません'}>
                  <span>
                    <LFCIconButton
                      color="primary"
                      size="small"
                      onClick={onDownloadBefore ?? undefined}
                      disabled={!onDownloadBefore}
                    >
                      <DownloadIcon fontSize="small" />
                    </LFCIconButton>
                  </span>
                </Tooltip>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1,
                  bgcolor: 'success.main',
                  color: 'success.contrastText'
                }}
              >
                <Typography variant="body1">After</Typography>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                  <Tooltip title={onDownloadAfter ? 'ダウンロード' : 'ファイルが存在しません'}>
                    <span>
                      <LFCIconButton
                        color="primary"
                        size="small"
                        onClick={onDownloadAfter ?? undefined}
                        disabled={!onDownloadAfter}
                      >
                        <DownloadIcon fontSize="small" />
                      </LFCIconButton>
                    </span>
                  </Tooltip>
                  {onAiExplain && (
                    <Tooltip title={aiOpen ? 'AI解説を閉じる' : '差分のAI解説'}>
                      <span>
                        <LFCIconButton
                          color={aiOpen ? 'secondary' : 'primary'}
                          size="small"
                          onClick={onAiExplain}
                          disabled={aiLoading}
                        >
                          {aiLoading ? (
                            <CircularProgress size={18} />
                          ) : (
                            <AutoStoriesIcon fontSize="small" />
                          )}
                        </LFCIconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>
            {/* コンテンツエリア（サイドバー + 差分表示） */}
            <Box sx={{display: 'flex', flex: 1, overflow: 'hidden'}}>
              {/* Before側サイドバー */}
              {beforeSidebar && (
                <Box
                  sx={{
                    width: 150,
                    overflow: 'auto',
                    borderRight: 1,
                    borderColor: isDark ? 'grey.700' : 'grey.300',
                    flexShrink: 0
                  }}
                >
                  {beforeSidebar}
                </Box>
              )}
              {/* 差分コンテンツ */}
              <Box
                sx={{
                  maxHeight,
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: `${fontSize}px`,
                  flex: 1,
                  bgcolor: isDark ? 'grey.900' : 'grey.100',
                  position: 'relative'
                }}
              >
                {loading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(isDark ? '#000' : '#fff', 0.7),
                      zIndex: 1
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
                {diffLines.map((line, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      bgcolor:
                        line.type === 'add'
                          ? alpha(theme.palette.success.main, 0.15)
                          : line.type === 'delete'
                            ? alpha(theme.palette.error.main, 0.15)
                            : isDark
                              ? 'grey.900'
                              : 'grey.100',
                      borderLeft: 4,
                      borderColor:
                        line.type === 'add'
                          ? 'success.main'
                          : line.type === 'delete'
                            ? 'error.main'
                            : 'transparent',
                      '&:hover': {
                        bgcolor:
                          line.type === 'add'
                            ? alpha(theme.palette.success.main, 0.25)
                            : line.type === 'delete'
                              ? alpha(theme.palette.error.main, 0.25)
                              : alpha(theme.palette.primary.main, 0.08)
                      }
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexShrink: 0,
                        bgcolor: isDark ? 'grey.800' : 'grey.200',
                        borderRight: 1,
                        borderColor: 'divider'
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: '4em',
                          color: 'text.secondary',
                          textAlign: 'right',
                          px: 2,
                          py: 0.25,
                          userSelect: 'none'
                        }}
                      >
                        {line.beforeLineNum || ' '}
                      </Box>
                      <Box
                        sx={{
                          minWidth: '4em',
                          color: 'text.secondary',
                          textAlign: 'right',
                          px: 2,
                          py: 0.25,
                          userSelect: 'none'
                        }}
                      >
                        {line.afterLineNum || ' '}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        width: 20,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color:
                          line.type === 'add'
                            ? 'success.main'
                            : line.type === 'delete'
                              ? 'error.main'
                              : 'text.secondary',
                        fontWeight: line.type !== 'same' ? 'bold' : 'normal'
                      }}
                    >
                      {line.type === 'add' ? '+' : line.type === 'delete' ? '-' : ' '}
                    </Box>
                    <Box
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        flex: 1,
                        py: 0.25,
                        pr: 1
                      }}
                    >
                      {renderLineContent(line.content)}
                    </Box>
                  </Box>
                ))}
              </Box>
              {/* After側サイドバー */}
              {afterSidebar && (
                <Box
                  sx={{
                    width: 150,
                    overflow: 'auto',
                    borderLeft: 1,
                    borderColor: isDark ? 'grey.700' : 'grey.300',
                    flexShrink: 0
                  }}
                >
                  {afterSidebar}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Side-by-Side表示（横並び） */}
        {viewMode === 'sideBySide' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              overflow: 'hidden'
            }}
          >
            {/* 固定ヘッダー */}
            <Box sx={{display: 'flex', flexShrink: 0}}>
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1,
                  bgcolor: 'error.main',
                  color: 'error.contrastText',
                  borderRight: 1,
                  borderRightColor: isDark ? 'grey.700' : 'grey.300'
                }}
              >
                <Typography variant="body1">Before</Typography>
                <Tooltip title={onDownloadBefore ? 'ダウンロード' : 'ファイルが存在しません'}>
                  <span>
                    <LFCIconButton
                      color="primary"
                      size="small"
                      onClick={onDownloadBefore ?? undefined}
                      disabled={!onDownloadBefore}
                    >
                      <DownloadIcon fontSize="small" />
                    </LFCIconButton>
                  </span>
                </Tooltip>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1,
                  bgcolor: 'success.main',
                  color: 'success.contrastText'
                }}
              >
                <Typography variant="body1">After</Typography>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                  <Tooltip title={onDownloadAfter ? 'ダウンロード' : 'ファイルが存在しません'}>
                    <span>
                      <LFCIconButton
                        color="primary"
                        size="small"
                        onClick={onDownloadAfter ?? undefined}
                        disabled={!onDownloadAfter}
                      >
                        <DownloadIcon fontSize="small" />
                      </LFCIconButton>
                    </span>
                  </Tooltip>
                  {onAiExplain && (
                    <Tooltip title={aiOpen ? 'AI解説を閉じる' : '差分のAI解説'}>
                      <span>
                        <LFCIconButton
                          color={aiOpen ? 'secondary' : 'primary'}
                          size="small"
                          onClick={onAiExplain}
                          disabled={aiLoading}
                        >
                          {aiLoading ? (
                            <CircularProgress size={18} />
                          ) : (
                            <AutoStoriesIcon fontSize="small" />
                          )}
                        </LFCIconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>
            {/* コンテンツエリア（サイドバー + 差分表示） */}
            <Box sx={{display: 'flex', flex: 1, overflow: 'hidden'}}>
              {/* Before側サイドバー */}
              {beforeSidebar && (
                <Box
                  sx={{
                    width: 150,
                    overflow: 'auto',
                    borderRight: 1,
                    borderColor: isDark ? 'grey.700' : 'grey.300',
                    flexShrink: 0
                  }}
                >
                  {beforeSidebar}
                </Box>
              )}
              {/* スクロール可能な差分コンテンツ */}
              <Box
                sx={{
                  maxHeight,
                  overflow: 'auto',
                  flex: 1,
                  bgcolor: isDark ? 'grey.900' : 'grey.100',
                  fontFamily: 'monospace',
                  fontSize: `${fontSize}px`,
                  position: 'relative'
                }}
              >
                {loading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(isDark ? '#000' : '#fff', 0.7),
                      zIndex: 1
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
                {sideBySideLines.map((line, idx) => (
                  <Box
                    key={idx}
                    onMouseEnter={() => setHoveredLine(idx)}
                    onMouseLeave={() => setHoveredLine(null)}
                    sx={{display: 'flex'}}
                  >
                    {/* Before側 */}
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        py: 0.25,
                        minHeight: '1.5em',
                        bgcolor:
                          hoveredLine === idx
                            ? line.before.type === 'delete'
                              ? alpha(theme.palette.error.main, 0.25)
                              : alpha(theme.palette.primary.main, 0.08)
                            : line.before.type === 'delete'
                              ? alpha(theme.palette.error.main, 0.15)
                              : isDark
                                ? 'grey.900'
                                : 'grey.100',
                        borderLeft: 3,
                        borderColor: line.before.type === 'delete' ? 'error.main' : 'transparent',
                        borderRight: 1,
                        borderRightColor: isDark ? 'grey.700' : 'grey.300'
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: '4em',
                          flexShrink: 0,
                          color: 'text.secondary',
                          textAlign: 'right',
                          px: 2,
                          py: 0.25,
                          my: -0.25,
                          bgcolor: isDark ? 'grey.800' : 'grey.200',
                          borderRight: 1,
                          borderColor: 'divider',
                          userSelect: 'none'
                        }}
                      >
                        {line.before.lineNum || ' '}
                      </Box>
                      <Box
                        sx={{
                          width: 20,
                          flexShrink: 0,
                          color: 'error.main',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          userSelect: 'none'
                        }}
                      >
                        {line.before.type === 'delete' ? '-' : ' '}
                      </Box>
                      <Box sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-all', flex: 1, pr: 1}}>
                        {renderLineContent(line.before.content)}
                      </Box>
                    </Box>
                    {/* After側 */}
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        py: 0.25,
                        minHeight: '1.5em',
                        bgcolor:
                          hoveredLine === idx
                            ? line.after.type === 'add'
                              ? alpha(theme.palette.success.main, 0.25)
                              : alpha(theme.palette.primary.main, 0.08)
                            : line.after.type === 'add'
                              ? alpha(theme.palette.success.main, 0.15)
                              : isDark
                                ? 'grey.900'
                                : 'grey.100',
                        borderLeft: 3,
                        borderColor: line.after.type === 'add' ? 'success.main' : 'transparent'
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: '4em',
                          flexShrink: 0,
                          color: 'text.secondary',
                          textAlign: 'right',
                          px: 2,
                          py: 0.25,
                          my: -0.25,
                          bgcolor: isDark ? 'grey.800' : 'grey.200',
                          borderRight: 1,
                          borderColor: 'divider',
                          userSelect: 'none'
                        }}
                      >
                        {line.after.lineNum || ' '}
                      </Box>
                      <Box
                        sx={{
                          width: 20,
                          flexShrink: 0,
                          color: 'success.main',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          userSelect: 'none'
                        }}
                      >
                        {line.after.type === 'add' ? '+' : ' '}
                      </Box>
                      <Box sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-all', flex: 1, pr: 1}}>
                        {renderLineContent(line.after.content)}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
              {/* After側サイドバー */}
              {afterSidebar && (
                <Box
                  sx={{
                    width: 150,
                    overflow: 'auto',
                    borderLeft: 1,
                    borderColor: isDark ? 'grey.700' : 'grey.300',
                    flexShrink: 0
                  }}
                >
                  {afterSidebar}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    );
  }
);

export default DiffDisplay;
